
<template>
  <div class="stock-supplier-access stock-count-card">
    <div class="card">
      <div class="card-content">
        <div class="row">
          <div class="col s12 m9">
            <span class="card-title">
              Supplier Access
            </span>

            <p class="grey-text text-darken-2">
              Select the suppliers available to each venue,
              then tick which suppliers can provide each
              product group. Choose the preferred supplier
              in the Primary column.
            </p>
          </div>

          <div class="col s12 m3 right-align">
            <button
              type="button"
              class="btn-flat waves-effect"
              :disabled="loading || !!savingId"
              @click="load"
            >
              <i class="material-icons left">
                refresh
              </i>
              Refresh
            </button>
          </div>
        </div>

        <div v-if="loading" class="progress">
          <div class="indeterminate"></div>
        </div>

        <div
          v-if="error"
          class="card-panel red lighten-4 red-text text-darken-4"
          role="alert"
        >
          {{ error }}
        </div>

        <p
          v-if="!loading && supplierNames.length"
          class="grey-text text-darken-1"
        >
          {{ supplierNames.length }} stock suppliers ·
          {{ productGroups.length }} product groups
        </p>
      </div>
    </div>

    <template v-if="!loading">
      <div
        v-for="venue in venues"
        :key="venue.id"
        class="card"
      >
        <div class="card-content">
          <div class="row">
            <div class="col s12 m9">
              <span class="card-title">
                {{ venue.name }}
              </span>

              <p
                v-if="!configured[venue.id]"
                class="grey-text text-darken-1"
              >
                Not configured — all suppliers currently allowed.
              </p>

              <p
                v-else
                class="grey-text text-darken-1"
              >
                {{ selectedCount(venue.id) }}
                venue suppliers enabled ·
                {{ overrideCount(venue.id) }}
                group overrides
              </p>
            </div>

            <div class="col s12 m3 right-align">
              <button
                type="button"
                class="btn teal darken-2 waves-effect"
                :disabled="!!savingId"
                @click="save(venue.id)"
              >
                <i class="material-icons left">
                  save
                </i>

                {{
                  savingId === venue.id
                    ? "Saving..."
                    : "Save"
                }}
              </button>
            </div>
          </div>

          <strong>Enabled suppliers for this venue</strong>

          <div class="row">
            <div
              v-for="supplier in supplierNames"
              :key="supplier"
              class="col s12 m3"
            >
              <p>
                <label>
                  <input
                    type="checkbox"
                    class="filled-in"
                    :checked="
                      venueSupplierChecked(
                        venue.id,
                        supplier
                      )
                    "
                    :disabled="!!savingId"
                    @change="
                      toggleVenueSupplier(
                        venue.id,
                        supplier,
                        $event.target.checked
                      )
                    "
                  />

                  <span>
                    {{ supplier }}
                  </span>
                </label>
              </p>
            </div>
          </div>

          <div class="divider"></div>

          <button
            type="button"
            class="btn-flat waves-effect"
            :aria-expanded="!!expanded[venue.id]"
            @click="toggleExpanded(venue.id)"
          >
            <i class="material-icons left">
              {{
                expanded[venue.id]
                  ? "expand_less"
                  : "expand_more"
              }}
            </i>

            Product Group Permissions
            ({{ productGroups.length }})
          </button>

          <div v-if="expanded[venue.id]">
            <p class="grey-text text-darken-1">
              Tick the supplier for each product group.
              The Primary supplier is used as the
              preferred source, while other checked
              suppliers remain permitted alternatives.
            </p>

            <div
              style="width: 100%; overflow-x: auto;"
            >
              <table
                class="striped highlight"
                style="min-width: 850px;"
              >
                <thead>
                  <tr>
                    <th>
                      Product Group
                    </th>

                    <th
                      v-for="supplier in supplierNames"
                      :key="supplier"
                      class="center-align"
                    >
                      <div>
                        {{ supplier }}
                      </div>

                      <label>
                        <input
                          type="checkbox"
                          class="filled-in"
                          :checked="
                            allGroupsChecked(
                              venue.id,
                              supplier
                            )
                          "
                          :disabled="
                            !!savingId ||
                            !venueSupplierChecked(
                              venue.id,
                              supplier
                            )
                          "
                          @change="
                            toggleAllGroups(
                              venue.id,
                              supplier,
                              $event.target.checked
                            )
                          "
                        />

                        <span>
                          All groups
                        </span>
                      </label>
                    </th>

                    <th>
                      Primary Supplier
                    </th>
                  </tr>
                </thead>

                <tbody>
                  <tr
                    v-for="group in productGroups"
                    :key="group"
                  >
                    <td>
                      <strong>
                        {{ group }}
                      </strong>

                      <button
                        v-if="
                          hasGroupOverride(
                            venue.id,
                            group
                          )
                        "
                        type="button"
                        class="btn-flat"
                        title="Reset this group to venue defaults"
                        :disabled="!!savingId"
                        @click="
                          resetGroup(
                            venue.id,
                            group
                          )
                        "
                      >
                        <i class="material-icons">
                          settings_backup_restore
                        </i>
                      </button>
                    </td>

                    <td
                      v-for="supplier in supplierNames"
                      :key="supplier"
                      class="center-align"
                    >
                      <label
                        v-if="
                          supplierHasGroupProducts(
                            supplier,
                            group
                          )
                        "
                      >
                        <input
                          type="checkbox"
                          class="filled-in"
                          :checked="
                            groupSupplierChecked(
                              venue.id,
                              group,
                              supplier
                            )
                          "
                          :disabled="
                            !!savingId ||
                            !venueSupplierChecked(
                              venue.id,
                              supplier
                            )
                          "
                          @change="
                            toggleGroupSupplier(
                              venue.id,
                              group,
                              supplier,
                              $event.target.checked
                            )
                          "
                        />

                        <span></span>
                      </label>

                      <span
                        v-else
                        class="grey-text text-lighten-1"
                      >
                        —
                      </span>
                    </td>

                    <td>
                      <select
                        class="browser-default"
                        style="min-width: 155px;"
                        :value="
                          groupPrimary(
                            venue.id,
                            group
                          )
                        "
                        :disabled="
                          !!savingId ||
                          !effectiveGroupSuppliers(
                            venue.id,
                            group
                          ).length
                        "
                        @change="
                          setGroupPrimary(
                            venue.id,
                            group,
                            $event.target.value
                          )
                        "
                      >
                        <option
                          v-if="
                            !effectiveGroupSuppliers(
                              venue.id,
                              group
                            ).length
                          "
                          value=""
                        >
                          None
                        </option>

                        <option
                          v-for="supplier in effectiveGroupSuppliers(
                            venue.id,
                            group
                          )"
                          :key="supplier"
                          :value="supplier"
                        >
                          {{ supplier }}
                        </option>
                      </select>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p class="grey-text text-darken-1">
              A dash means the supplier has no stock
              products in that group. Disabled checkboxes
              mean the supplier is not enabled for the venue.
              Use All groups to enable or disable an
              entire supplier column.
            </p>
          </div>

          <p
            v-if="messages[venue.id]"
            class="teal-text text-darken-2"
            role="status"
          >
            {{ messages[venue.id] }}
          </p>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import {
  onMounted,
  reactive,
  ref,
} from "vue";

import {
  fetchStockOptions,
} from "@/scripts/sqlApiHelper/stockOptionsApi";

import {
  fetchStockUnits,
} from "@/scripts/sqlApiHelper/stockUnitsApi";

import {
  listVenueSupplierAccess,
  normaliseGroupRules,
  normaliseSupplierNames,
  saveVenueSupplierAccess,
  supplierKey,
} from "@/scripts/sqlApiHelper/venueSuppliersApi";

const loading = ref(false);
const savingId = ref("");
const error = ref("");

const venues = ref([]);
const supplierNames = ref([]);
const productGroups = ref([]);
const stockItems = ref([]);

const venueSuppliers = reactive({});
const groupRules = reactive({});
const configured = reactive({});
const messages = reactive({});
const expanded = reactive({});

function same(a, b) {
  return supplierKey(a) === supplierKey(b);
}

function includesName(values, name) {
  return (values || []).some(
    (value) => same(value, name)
  );
}

function selectedCount(venueId) {
  return normaliseSupplierNames(
    venueSuppliers[venueId]
  ).length;
}

function overrideCount(venueId) {
  return Object.keys(
    groupRules[venueId] || {}
  ).length;
}

function toggleExpanded(venueId) {
  expanded[venueId] = !expanded[venueId];
}

function venueSupplierChecked(
  venueId,
  supplier
) {
  return includesName(
    venueSuppliers[venueId],
    supplier
  );
}

function toggleVenueSupplier(
  venueId,
  supplier,
  checked
) {
  const current =
    venueSuppliers[venueId] || [];

  const next = current.filter(
    (name) => !same(name, supplier)
  );

  if (checked) {
    next.push(supplier);
  }

  venueSuppliers[venueId] =
    supplierNames.value.filter(
      (name) => includesName(next, name)
    );

  messages[venueId] = "";
}

function stockedSuppliers(group) {
  const names = normaliseSupplierNames(
    stockItems.value
      .filter(
        (item) =>
          same(item.product_group, group)
      )
      .map((item) => item.supplier)
  );

  return supplierNames.value.filter(
    (supplier) =>
      includesName(names, supplier)
  );
}

function supplierHasGroupProducts(
  supplier,
  group
) {
  return includesName(
    stockedSuppliers(group),
    supplier
  );
}

function availableGroupSuppliers(
  venueId,
  group
) {
  const enabled =
    venueSuppliers[venueId] || [];

  return stockedSuppliers(group).filter(
    (supplier) =>
      includesName(enabled, supplier)
  );
}

function hasGroupOverride(
  venueId,
  group
) {
  return Object.prototype.hasOwnProperty.call(
    groupRules[venueId] || {},
    group
  );
}

function effectiveGroupSuppliers(
  venueId,
  group
) {
  const available =
    availableGroupSuppliers(
      venueId,
      group
    );

  if (!hasGroupOverride(venueId, group)) {
    return available;
  }

  const rule =
    groupRules[venueId][group];

  return available.filter(
    (supplier) =>
      includesName(
        rule.suppliers,
        supplier
      )
  );
}

function groupSupplierChecked(
  venueId,
  group,
  supplier
) {
  return includesName(
    effectiveGroupSuppliers(
      venueId,
      group
    ),
    supplier
  );
}

function groupPrimary(
  venueId,
  group
) {
  const allowed =
    effectiveGroupSuppliers(
      venueId,
      group
    );

  const requested =
    groupRules[venueId]?.[group]
      ?.primarySupplier;

  return allowed.find(
    (supplier) =>
      same(supplier, requested)
  ) ||
    allowed[0] ||
    "";
}

function setGroupRule(
  venueId,
  group,
  allowedNames,
  primaryName
) {
  const available =
    availableGroupSuppliers(
      venueId,
      group
    );

  const suppliers = available.filter(
    (supplier) =>
      includesName(
        allowedNames,
        supplier
      )
  );

  const primarySupplier =
    suppliers.find(
      (supplier) =>
        same(supplier, primaryName)
    ) ||
    suppliers[0] ||
    "";

  groupRules[venueId] = {
    ...(groupRules[venueId] || {}),

    [group]: {
      suppliers,
      primarySupplier,
    },
  };

  messages[venueId] = "";
}

function toggleGroupSupplier(
  venueId,
  group,
  supplier,
  checked
) {
  if (
    !venueSupplierChecked(
      venueId,
      supplier
    ) ||
    !supplierHasGroupProducts(
      supplier,
      group
    )
  ) {
    return;
  }

  const current =
    effectiveGroupSuppliers(
      venueId,
      group
    );

  const previousPrimary =
    groupPrimary(
      venueId,
      group
    );

  const next = current.filter(
    (name) =>
      !same(name, supplier)
  );

  if (checked) {
    next.push(supplier);
  }

  setGroupRule(
    venueId,
    group,
    next,
    previousPrimary
  );
}

function setGroupPrimary(
  venueId,
  group,
  supplier
) {
  const allowed =
    effectiveGroupSuppliers(
      venueId,
      group
    );

  if (!includesName(allowed, supplier)) {
    return;
  }

  setGroupRule(
    venueId,
    group,
    allowed,
    supplier
  );
}

function resetGroup(
  venueId,
  group
) {
  const next = {
    ...(groupRules[venueId] || {}),
  };

  delete next[group];

  groupRules[venueId] = next;
  messages[venueId] = "";
}

function applicableGroups(
  venueId,
  supplier
) {
  if (
    !venueSupplierChecked(
      venueId,
      supplier
    )
  ) {
    return [];
  }

  return productGroups.value.filter(
    (group) =>
      supplierHasGroupProducts(
        supplier,
        group
      )
  );
}

function allGroupsChecked(
  venueId,
  supplier
) {
  const groups =
    applicableGroups(
      venueId,
      supplier
    );

  return (
    groups.length > 0 &&
    groups.every(
      (group) =>
        groupSupplierChecked(
          venueId,
          group,
          supplier
        )
    )
  );
}

function toggleAllGroups(
  venueId,
  supplier,
  checked
) {
  const groups =
    applicableGroups(
      venueId,
      supplier
    );

  for (const group of groups) {
    const current =
      groupSupplierChecked(
        venueId,
        group,
        supplier
      );

    if (current !== checked) {
      toggleGroupSupplier(
        venueId,
        group,
        supplier,
        checked
      );
    }
  }
}

function rulesForSave(venueId) {
  const result = {};

  for (const [
    group,
    rule,
  ] of Object.entries(
    groupRules[venueId] || {}
  )) {
    const available =
      availableGroupSuppliers(
        venueId,
        group
      );

    const suppliers =
      available.filter(
        (name) =>
          includesName(
            rule.suppliers,
            name
          )
      );

    const primarySupplier =
      suppliers.find(
        (name) =>
          same(
            name,
            rule.primarySupplier
          )
      ) ||
      suppliers[0] ||
      "";

    result[group] = {
      suppliers,
      primarySupplier,
    };
  }

  return normaliseGroupRules(result);
}

async function load() {
  if (loading.value || savingId.value) {
    return;
  }

  loading.value = true;
  error.value = "";

  try {
    const [
      options,
      products,
      venueRows,
    ] = await Promise.all([
      fetchStockOptions(),
      fetchStockUnits(),
      listVenueSupplierAccess(),
    ]);

    stockItems.value =
      Array.isArray(products)
        ? products
        : [];

    supplierNames.value =
      normaliseSupplierNames([
        ...(options?.suppliers || []),

        ...stockItems.value.map(
          (item) => item.supplier
        ),
      ]);

    const groups =
      normaliseSupplierNames([
        ...(options?.productGroups || []),

        ...stockItems.value.map(
          (item) => item.product_group
        ),
      ]);

    // Show only product groups with actual
    // stock records.
    productGroups.value =
      groups.filter(
        (group) =>
          stockItems.value.some(
            (item) =>
              same(
                item.product_group,
                group
              )
          )
      );

    venues.value =
      Array.isArray(venueRows)
        ? venueRows
        : [];

    for (const [
      index,
      venue,
    ] of venues.value.entries()) {
      venueSuppliers[venue.id] =
        venue.configured
          ? normaliseSupplierNames(
              venue.suppliers
            )
          : [...supplierNames.value];

      groupRules[venue.id] =
        normaliseGroupRules(
          venue.groupRules
        );

      configured[venue.id] =
        venue.configured === true;

      messages[venue.id] = "";

      // Only expand the first venue by default.
      expanded[venue.id] =
        index === 0;
    }
  } catch (cause) {
    console.error(
      "[StockSupplierAccess] Load failed:",
      cause
    );

    error.value =
      cause?.message ||
      "Unable to load supplier permissions.";
  } finally {
    loading.value = false;
  }
}

async function save(venueId) {
  if (savingId.value) {
    return;
  }

  savingId.value = venueId;
  messages[venueId] = "";

  try {
    const suppliers =
      normaliseSupplierNames(
        venueSuppliers[venueId]
      );

    const rules =
      rulesForSave(venueId);

    const savedSuppliers =
      await saveVenueSupplierAccess(
        venueId,
        suppliers,
        rules
      );

    venueSuppliers[venueId] = [
      ...savedSuppliers
    ];

    groupRules[venueId] = rules;

    configured[venueId] = true;

    messages[venueId] =
      "Supplier permissions saved successfully.";
  } catch (cause) {
    console.error(
      "[StockSupplierAccess] Save failed:",
      cause
    );

    messages[venueId] =
      cause?.message ||
      "Unable to save supplier permissions.";
  } finally {
    savingId.value = "";
  }
}

onMounted(load);
</script>
